import { useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import MandatorySign from "../../Components/MandatorySign";
import ErrorMessage from "../../Components/ErrorMessage";
import Spinner from "../../Components/Spinner";
import AgentLoader from "../../Loaders/AgentLoader";
import { useParams } from "react-router-dom";
import GeneralInput from "./GeneralInput";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const InputAgent = ({
    orderedNodes,
    executeNode,
    handleClose,
    handleSelect,
    selectedItem,
    selectedNode,
    mcpData,
    fetchMcpData,
    loadingMcpData,
    executionHistory
}) => {
    const { org_id } = useParams()
    const textareaRef = useRef(null);
    const [customName, setCustomName] = useState("")
    const [models, setModels] = useState([])
    const [model, setModel] = useState();
    const [memory, setMemory] = useState(false);
    const [systemPrompt, setSystemPrompt] = useState("");
    const [loadingData, setLoadingData] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedPlugin, setSelectedPlugin] = useState(null)

    const [errorMessage, setErrorMessage] = useState({
        message: "",
        display: false
    });


    const handleConfirm = () => {
        if (!customName.trim()) {
            setErrorMessage({ message: "Custom name is required", display: true });
            return;
        }

        if (!model?.id) {
            setErrorMessage({ message: "Model is required", display: true });
            return;
        }

        if (!systemPrompt) {
            setErrorMessage({ message: "System prompt is required", display: true });
            return;
        }

        handleSelect(selectedItem.value, selectedItem, {
            custom_name: customName,
            model: model?.id,
            memory: memory,
            plugin: selectedPlugin?.id,
            system_prompt: systemPrompt,
        });
    };

    const handleTextareaInput = (e) => {
        const el = textareaRef.current;
        if (!el) return;

        el.style.height = "80px";

        if (el.value.trim() === "") {
            el.style.height = "80px";
            return;
        }

        el.style.height = `${el.scrollHeight}px`;
    };

    const fetchData = async () => {
        setLoadingData(true);

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_models`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    limit: 20
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setModels(result.data || []);
        } catch (err) {
            setLoadingData(false);
            console.error("Error fetching models:", err);
        } finally {
            setTimeout(() => {
                setLoadingData(false);
            }, 1000);
        }
    };

    useEffect(() => {
        fetchMcpData(org_id)
        if (!loadingMcpData && mcpData) {
            fetchData();
        }

    }, [selectedItem.value]);

    useEffect(() => {
        if (!selectedNode) return;

        const params = selectedNode?.data?.params;
        if (!params) return;

        setMemory(params.memory ?? false);
        setSystemPrompt(params.system_prompt ?? "");
        setCustomName(params.custom_name)

        // model
        if (params.model && models.length > 0) {
            const foundModel = models.find(m => m.id === params.model);
            if (foundModel) setModel(foundModel);
        }

        // plugin
        if (params.plugin) {
            const foundPlugin = mcpData?.find(p => p.id === params.plugin);
            if (foundPlugin) setSelectedPlugin(foundPlugin);
        }
    }, [selectedNode, models, mcpData]);

    return (
        <>
            {loadingData ? (
                <AgentLoader />
            ) : (
                <div className="modal_main_cont">
                    <GeneralInput
                        orderedNodes={orderedNodes}
                        selectedNode={selectedNode}
                        executeNode={executeNode}
                        executionHistory={executionHistory}
                        onSelectValue={(val) => setSystemPrompt(val)}
                    />

                    <div className="label_input">
                        <label className="input_label">
                            Custom name <MandatorySign message="Custom name is required" />
                        </label>

                        <input
                            type="text"
                            className="register_form_first fade-in"
                            value={customName}
                            placeholder="Ex: Email creator"
                            onChange={(e) => setCustomName(e.target.value)}
                        />
                    </div>

                    <div className="label_input">
                        <label className="input_label">
                            Model <MandatorySign message="Model is required" />
                        </label>

                        <Dropdown drop="down" autoClose="outside">
                            <Dropdown.Toggle as="div" className="register_form_first fade-in pointer">
                                {model?.name ? (
                                    `
                                    ${model?.name} ${model?.inference_provider}
                                    `
                                ) : (
                                    "Select a model"
                                )}
                            </Dropdown.Toggle>

                            <Dropdown.Menu className="custom_dropdown_menu">
                                <div className="scrollable_dropdown_cont">
                                    {models.map((m) => (
                                        <div
                                            key={m.id}
                                            className="settings_link"
                                            onClick={() => setModel(m)}
                                        >
                                            {m.name} {m.inference_provider}
                                        </div>
                                    ))}
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>

                    <div className="label_input">
                        <label className="input_label">
                            Memory <MandatorySign message="Memory allows allows the LLM to store the conversation and have memory" icon="info" />
                        </label>

                        <Dropdown drop="down" autoClose="outside">
                            <Dropdown.Toggle as="div" className="register_form_first fade-in pointer">
                                {memory ? "Active" : "Non active"}
                            </Dropdown.Toggle>

                            <Dropdown.Menu className="custom_dropdown_menu">
                                <div
                                    key={true}
                                    className={`settings_link ${memory && "settings_link_selected"}`}
                                    onClick={() => setMemory(true)}
                                >
                                    Active
                                </div>
                                <div
                                    key={false}
                                    className={`settings_link ${!memory && "settings_link_selected"}`}
                                    onClick={() => setMemory(false)}
                                >
                                    Non active
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>

                    <div className="label_input">
                        <label className="input_label">
                            Plugins <MandatorySign message="Plugins (MCPs) can be triggered by the AI if active" icon="info" />
                        </label>

                        {mcpData.length > 0 ? (
                            <Dropdown drop="down" autoClose="outside">
                                <Dropdown.Toggle as="div" className="register_form_first fade-in pointer">
                                    {selectedPlugin?.label || "Select plugin"}
                                </Dropdown.Toggle>

                                <Dropdown.Menu className="custom_dropdown_menu">
                                    {mcpData.map((item) => (
                                        <div
                                            key={item.id}
                                            className="settings_link"
                                            onClick={() => setSelectedPlugin(item.id)}
                                        >
                                            {item.name}
                                        </div>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        ) : (
                            <p className="register_form_first register_form_first_disabled fade-in">No plugins available</p>

                        )}
                    </div>

                    <div className="label_input">
                        <label className="input_label">
                            System Prompt <MandatorySign message="System prompt is required" />
                        </label>

                        <textarea
                            ref={textareaRef}
                            className="register_form_first textarea_reg fade-in"
                            rows={6}
                            placeholder="Describe what you want the AI to do. Include goals, tone, and any specific instructions..."
                            value={systemPrompt}
                            onChange={(e) => {
                                setSystemPrompt(e.target.value);
                                handleTextareaInput(e);
                            }}
                        />
                    </div>

                </div>
            )}

            <div className="modal_footer">
                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>

                <div className="modal_footer_btn_cont">
                    <button
                        className="modale_save_btn"
                        onClick={handleConfirm}
                    >
                        Create agent
                    </button>

                    <button
                        className="modale_close_btn"
                        onClick={handleClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </>
    );
};

export default InputAgent;